import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { BiRefresh } from "react-icons/bi";
import {
  FaChartLine,
  FaChartBar,
  FaGlobeAsia,
  FaTags,
  FaUserClock,
  FaTasks,
  FaUsers,
  FaInbox,
  FaReply,
  FaClock,
  FaBolt,
} from "react-icons/fa";
import { MdSource, MdOutlineFlag, MdQueryStats, MdInbox, MdErrorOutline } from "react-icons/md";
import axios from "axios";

import NavMenu from "../components/NavMenu";
import { useTheme } from "../components/ThemeContext";
import "./analyticsPage.css";

const API = process.env.REACT_APP_API || "";

const ACCENT = {
  blue: { light: "#1a3483", dark: "#3987e5", tint: "#e6ebf8" },
  violet: { light: "#6d3fae", dark: "#7c3aed", tint: "#efe7fa" },
  teal: { light: "#0d9488", dark: "#199e70", tint: "#e2f4f2" },
  green: { light: "#128c4a", dark: "#4ade80", tint: "#e2f6ea" },
  magenta: { light: "#b0245f", dark: "#d55181", tint: "#fbe7ef" },
};

const RANGE_OPTIONS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const formatCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000) {
    const k = v / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K`;
  }
  return v.toLocaleString();
};

// Fractional days -> "Xh Ym" (e.g. 0.3 days -> "7h 12m"). Under an hour it
// shows just minutes, and from a full day up it rolls hours into days
// ("1d 3h") so a long wait doesn't read as "51h 20m".
const formatDuration = (days) => {
  const totalMinutes = Math.round(Math.abs(Number(days) || 0) * 24 * 60);
  if (totalMinutes < 1) return "0m";

  const d = Math.floor(totalMinutes / (24 * 60));
  const h = Math.floor((totalMinutes % (24 * 60)) / 60);
  const m = totalMinutes % 60;

  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};

const niceCeil = (value) => {
  if (value <= 0) return 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const residual = value / magnitude;
  let niceResidual;
  if (residual <= 1) niceResidual = 1;
  else if (residual <= 2) niceResidual = 2;
  else if (residual <= 5) niceResidual = 5;
  else niceResidual = 10;
  return niceResidual * magnitude;
};

const formatShortDate = (isoDate) => {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const pad2 = (n) => String(n).padStart(2, "0");

// dd-mm-yyyy, like the rest of the app
const formatFullDate = (isoDate) => {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
};

// dd-mm-yyyy, HH:MM:SS (24-hour)
const formatTimestamp = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}, ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

const formatMonthShort = (ym) => {
  const d = new Date(`${ym}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return ym;
  return d.toLocaleDateString("en-GB", { month: "short" });
};

const formatMonthFull = (ym) => {
  const d = new Date(`${ym}-01T00:00:00`);
  if (Number.isNaN(d.getTime())) return ym;
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

const roundedTopBarPath = (x, y, w, h, r) => {
  const radius = Math.max(0, Math.min(r, w / 2, h));
  return `M ${x} ${y + h}
          L ${x} ${y + radius}
          Q ${x} ${y} ${x + radius} ${y}
          L ${x + w - radius} ${y}
          Q ${x + w} ${y} ${x + w} ${y + radius}
          L ${x + w} ${y + h}
          Z`;
};

const sum = (rows) => rows.reduce((t, r) => t + (Number(r.count) || 0), 0);

// delta: signed number. goodWhenUp: whether an increase counts as "good"
// (e.g. total enquiries up = good, avg response time up = bad).
const DeltaBadge = ({ delta, goodWhenUp = true, suffix = "%", format }) => {
  if (delta == null || Number.isNaN(delta)) return null;
  const isUp = delta > 0;
  const isFlat = delta === 0;
  const isGood = isFlat ? null : isUp === goodWhenUp;
  const cls = isFlat ? "anp-delta flat" : isGood ? "anp-delta good" : "anp-delta bad";
  const arrow = isFlat ? "→" : isUp ? "↑" : "↓";
  return (
    <span className={cls} title="Compared with the previous period">
      {arrow} {format ? format(Math.abs(delta)) : `${Math.abs(delta)}${suffix}`}
    </span>
  );
};

const StatTile = ({ label, value, note, icon, accent, delay, loading, delta, deltaGoodWhenUp, deltaSuffix, deltaFormat }) => (
  <div className={`anp-card anp-stat ${accent} anp-fade h-100`} style={{ "--d": `${delay}ms` }}>
    <div className="anp-stat-watermark" aria-hidden="true">{icon}</div>
    <div className="anp-stat-top">
      <span className="anp-stat-icon">{icon}</span>
      <span className="anp-stat-label">{label}</span>
      <DeltaBadge delta={delta} goodWhenUp={deltaGoodWhenUp} suffix={deltaSuffix} format={deltaFormat} />
    </div>
    {loading ? <span className="anp-skel anp-skel-value" /> : <div className="anp-stat-value">{value}</div>}
    <div className="anp-stat-note">{note}</div>
  </div>
);

const EmptyState = ({ text }) => (
  <div className="anp-empty">
    <span className="anp-empty-icon"><MdInbox /></span>
    <div>{text}</div>
  </div>
);

// A chart card with a Chart / Table switch so every chart has an accessible,
// non-visual twin.
const ChartCard = ({ title, subtitle, icon, accent, delay, tableRows, valueLabel = "Count", chips, loading, children }) => {
  const [view, setView] = useState("chart");
  const hasData = tableRows && tableRows.length > 0;
  const total = hasData ? tableRows.reduce((t, r) => t + (Number(r.value) || 0), 0) : 0;

  return (
    <section className={`anp-card anp-chart ${accent} anp-fade h-100`} style={{ "--d": `${delay}ms` }}>
      <header className="anp-chart-head">
        <span className="anp-title-icon">{icon}</span>
        <div className="anp-chart-titles">
          <h2 className="anp-chart-title">{title}</h2>
          {subtitle && <p className="anp-chart-sub">{subtitle}</p>}
        </div>

        {hasData && (
          <div className="anp-seg anp-seg-sm" role="group" aria-label={`${title} view`}>
            <button type="button" aria-pressed={view === "chart"} onClick={() => setView("chart")}>Chart</button>
            <button type="button" aria-pressed={view === "table"} onClick={() => setView("table")}>Table</button>
          </div>
        )}
      </header>

      {hasData && chips && chips.length > 0 && (
        <div className="anp-chips">
          {chips.map((c) => (
            <span className="anp-chip" key={c}>{c}</span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="anp-chart-skel" aria-busy="true">
          <span className="anp-skel" style={{ height: 14, width: "40%" }} />
          <span className="anp-skel" style={{ height: 150 }} />
        </div>
      ) : !hasData ? (
        <EmptyState text="No data available yet." />
      ) : view === "table" ? (
        <div className="anp-table-wrap">
          <table className="anp-table">
            <thead>
              <tr>
                <th>Label</th>
                <th className="text-end">{valueLabel}</th>
                <th className="text-end">Share</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={i}>
                  <td>{row.label}</td>
                  <td className="text-end anp-num">{row.value}</td>
                  <td className="text-end anp-muted">{total ? `${Math.round((row.value / total) * 100)}%` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}
    </section>
  );
};

const LineChart = ({ data, accentKey, isDark, height = 220 }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);
  const gradId = useRef(`anp-grad-${Math.random().toString(36).slice(2)}`).current;
  const width = 700;
  const padding = { top: 18, right: 16, bottom: 26, left: 34 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const accent = isDark ? ACCENT[accentKey].dark : ACCENT[accentKey].light;
  const gridColor = isDark ? "#3a3f44" : "#e2e8f0";
  const axisColor = isDark ? "#cbd5e1" : "#475569"; // >= 6:1 on the chart surface
  const surface = isDark ? "#212529" : "#ffffff";
  const labelColor = isDark ? "#e5e7eb" : "#111827";

  const maxVal = Math.max(1, ...data.map((d) => d.count));
  const yMax = niceCeil(maxVal);
  const peakIdx = data.reduce((best, d, i) => (d.count > data[best].count ? i : best), 0);

  const xFor = (i) => padding.left + (data.length <= 1 ? 0 : (i / (data.length - 1)) * plotW);
  const yFor = (v) => padding.top + plotH - (v / yMax) * plotH;

  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(d.count)}`).join(" ");
  const areaPath = `${linePath} L ${xFor(data.length - 1)} ${padding.top + plotH} L ${xFor(0)} ${padding.top + plotH} Z`;

  const ticks = [0, 0.5, 1].map((f) => Math.round(yMax * f));
  const xLabelIdx = [0, Math.floor((data.length - 1) / 2), data.length - 1];

  // Labelling every point of a 90-day series is unreadable: label all of them only
  // for short ranges, otherwise just the busiest day.
  const dense = data.length > 14;
  const showDots = data.length <= 31;
  const labelled = (i) => (dense ? i === peakIdx && data[i].count > 0 : true);

  const handleMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * width;
    let idx = Math.round(((relX - padding.left) / plotW) * (data.length - 1));
    idx = Math.max(0, Math.min(data.length - 1, idx));
    setHoverIdx(idx);
  };

  return (
    <div className="anp-chart-wrap">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="anp-svg"
        role="img"
        aria-label={`Line chart of daily enquiries, ${data.length} days, busiest day ${data[peakIdx]?.count ?? 0}`}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.32" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {ticks.map((t) => (
          <g key={t}>
            <line x1={padding.left} x2={width - padding.right} y1={yFor(t)} y2={yFor(t)} stroke={gridColor} strokeWidth="1" strokeDasharray={t === 0 ? undefined : "3 4"} />
            <text x={padding.left - 8} y={yFor(t) + 4} textAnchor="end" fontSize="12" fill={axisColor}>
              {t}
            </text>
          </g>
        ))}

        <path d={areaPath} fill={`url(#${gradId})`} stroke="none" />
        <path d={linePath} fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

        {data.map((d, i) => (
          <g key={`val-${i}`}>
            {(showDots || i === peakIdx) && (
              <circle cx={xFor(i)} cy={yFor(d.count)} r={i === peakIdx ? 4.5 : 3} fill={accent} stroke={surface} strokeWidth="2" />
            )}
            {labelled(i) && (
              <text
                x={xFor(i)}
                y={yFor(d.count) - 10}
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                fill={labelColor}
                stroke={surface}
                strokeWidth="3"
                paintOrder="stroke"
              >
                {d.count}
              </text>
            )}
          </g>
        ))}

        {hoverIdx !== null && data[hoverIdx] && (
          <>
            <line
              x1={xFor(hoverIdx)}
              x2={xFor(hoverIdx)}
              y1={padding.top}
              y2={padding.top + plotH}
              stroke={axisColor}
              strokeWidth="1"
              opacity="0.45"
            />
            <circle cx={xFor(hoverIdx)} cy={yFor(data[hoverIdx].count)} r="5" fill={accent} stroke={surface} strokeWidth="2" />
          </>
        )}

        {xLabelIdx.map((i) => (
          <text key={i} x={xFor(i)} y={height - 6} textAnchor="middle" fontSize="12" fill={axisColor}>
            {data[i] ? formatShortDate(data[i].date) : ""}
          </text>
        ))}
      </svg>

      {hoverIdx !== null && data[hoverIdx] && (
        <div
          className={`anp-tooltip ${isDark ? "dark" : ""}`}
          style={{
            left: `${(xFor(hoverIdx) / width) * 100}%`,
            top: `${(yFor(data[hoverIdx].count) / height) * 100}%`,
          }}
        >
          <div className="anp-tooltip-date">{formatFullDate(data[hoverIdx].date)}</div>
          <div className="anp-tooltip-value">{data[hoverIdx].count} enquiries</div>
        </div>
      )}
    </div>
  );
};

const VBarChart = ({ data, accentKey, isDark, height = 220 }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const width = 700;
  const padding = { top: 18, right: 16, bottom: 26, left: 34 };
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;

  const accent = isDark ? ACCENT[accentKey].dark : ACCENT[accentKey].light;
  const gridColor = isDark ? "#3a3f44" : "#e2e8f0";
  const axisColor = isDark ? "#cbd5e1" : "#475569"; // >= 6:1 on the chart surface
  const labelColor = isDark ? "#e5e7eb" : "#111827";

  const maxVal = Math.max(1, ...data.map((d) => d.count));
  const yMax = niceCeil(maxVal);
  const bandWidth = plotW / data.length;
  const barWidth = Math.min(24, bandWidth * 0.55);

  const yFor = (v) => padding.top + plotH - (v / yMax) * plotH;
  const xFor = (i) => padding.left + bandWidth * i + bandWidth / 2;

  const ticks = [0, 0.5, 1].map((f) => Math.round(yMax * f));

  return (
    <div className="anp-chart-wrap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="anp-svg"
        role="img"
        aria-label={`Bar chart of enquiries per month over the last ${data.length} months`}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padding.left} x2={width - padding.right} y1={yFor(t)} y2={yFor(t)} stroke={gridColor} strokeWidth="1" strokeDasharray={t === 0 ? undefined : "3 4"} />
            <text x={padding.left - 8} y={yFor(t) + 4} textAnchor="end" fontSize="12" fill={axisColor}>
              {t}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const barH = Math.max(plotH - (yFor(d.count) - padding.top), 1);
          const x = xFor(i) - barWidth / 2;
          const y = yFor(d.count);
          const isHover = hoverIdx === i;
          return (
            <g
              key={d.month}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              style={{ cursor: "pointer" }}
            >
              <rect x={x - 4} y={padding.top} width={barWidth + 8} height={plotH} fill="transparent" />
              <path d={roundedTopBarPath(x, y, barWidth, barH, 5)} fill={accent} opacity={isHover ? 1 : 0.85} />
              {d.count > 0 && (
                <text x={xFor(i)} y={y - 6} textAnchor="middle" fontSize="12" fontWeight="700" fill={labelColor}>
                  {d.count}
                </text>
              )}
              <text x={xFor(i)} y={height - 6} textAnchor="middle" fontSize="12" fill={axisColor}>
                {formatMonthShort(d.month)}
              </text>
            </g>
          );
        })}
      </svg>

      {hoverIdx !== null && (
        <div
          className={`anp-tooltip ${isDark ? "dark" : ""}`}
          style={{
            left: `${(xFor(hoverIdx) / width) * 100}%`,
            top: `${(yFor(data[hoverIdx].count) / height) * 100}%`,
          }}
        >
          <div className="anp-tooltip-date">{formatMonthFull(data[hoverIdx].month)}</div>
          <div className="anp-tooltip-value">{data[hoverIdx].count} enquiries</div>
        </div>
      )}
    </div>
  );
};

const HBarChart = ({ data, accentKey, isDark }) => {
  const accent = isDark ? ACCENT[accentKey].dark : ACCENT[accentKey].light;
  const max = Math.max(1, ...data.map((d) => d.count));
  const total = sum(data);

  return (
    <ul className="anp-hbar-list">
      {data.map((d) => (
        <li className="anp-hbar-row" key={d.name}>
          <div className="anp-hbar-label" title={d.name}>
            {d.name}
          </div>
          <div className="anp-hbar-track" role="presentation">
            <div
              className="anp-hbar-fill"
              style={{ width: `${Math.max((d.count / max) * 100, 3)}%`, background: accent }}
            />
          </div>
          <div className="anp-hbar-value">
            {d.count}
            <span className="anp-hbar-pct">{total ? `${Math.round((d.count / total) * 100)}%` : ""}</span>
          </div>
        </li>
      ))}
    </ul>
  );
};

const Analytics = () => {
  const { isDark } = useTheme();
  const [rangeDays, setRangeDays] = useState(30);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [filterMode, setFilterMode] = useState("preset"); // "preset" | "custom"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const fetchAnalytics = useCallback(async (params) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/getEnquiryAnalytics`, { params });
      if (res.data?.success) {
        setData(res.data.data || {});
      } else {
        setError(res.data?.message || "Failed to load analytics.");
      }
    } catch (err) {
      console.error("getEnquiryAnalytics error:", err);
      setError(err?.response?.data?.message || "Server error. Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }, []);

  const currentFetchParams = useCallback(
    () =>
      filterMode === "custom" && customFrom && customTo
        ? { from: customFrom, to: customTo }
        : { days: rangeDays },
    [filterMode, customFrom, customTo, rangeDays],
  );

  useEffect(() => {
    fetchAnalytics(currentFetchParams());
  }, [fetchAnalytics, currentFetchParams]);

  const handlePresetClick = (days) => {
    setCustomFrom("");
    setCustomTo("");
    setFilterMode("preset");
    setRangeDays(days);
  };

  const handleCustomFromChange = (value) => {
    setCustomFrom(value);
    setFilterMode(value && customTo ? "custom" : "preset");
  };

  const handleCustomToChange = (value) => {
    setCustomTo(value);
    setFilterMode(customFrom && value ? "custom" : "preset");
  };

  const clearCustomRange = () => {
    setCustomFrom("");
    setCustomTo("");
    setFilterMode("preset");
  };

  const kpis = data?.kpis || {};
  const dailyTrend = data?.dailyTrend || [];
  const monthlyTrend = data?.monthlyTrend || [];
  const bySource = data?.bySource || [];
  const byStatus = data?.byStatus || [];
  const byCountry = data?.byCountry || [];
  const byTag = data?.byTag || [];
  const byPriority = data?.byPriority || [];
  const responseTimeDistribution = data?.responseTimeDistribution || [];
  const byStaff = data?.byStaff || [];

  const toRows = (rows) => rows.map((d) => ({ label: d.name, value: d.count }));
  const dailyTableRows = dailyTrend.map((d) => ({ label: formatFullDate(d.date), value: d.count }));
  const monthlyTableRows = monthlyTrend.map((d) => ({ label: formatMonthFull(d.month), value: d.count }));

  const isCustomActive = filterMode === "custom" && !!customFrom && !!customTo;
  const rangeLabel = isCustomActive
    ? `${formatFullDate(data?.rangeStart || customFrom)} - ${formatFullDate(data?.rangeEnd || customTo)}`
    : RANGE_OPTIONS.find((r) => r.days === rangeDays)?.label || `${rangeDays} days`;
  const periodLabel = isCustomActive ? rangeLabel : `Last ${rangeLabel}`;

  const dailyChips = [];
  if (dailyTrend.length > 0) {
    const total = sum(dailyTrend);
    const peak = dailyTrend.reduce((b, d) => (d.count > b.count ? d : b), dailyTrend[0]);
    dailyChips.push(
      `Total ${total.toLocaleString()}`,
      `Avg ${(total / dailyTrend.length).toFixed(1)} / day`,
      `Peak ${peak.count} on ${formatShortDate(peak.date)}`,
    );
  }

  const monthlyChips = [];
  if (monthlyTrend.length > 0) {
    const best = monthlyTrend.reduce((b, d) => (d.count > b.count ? d : b), monthlyTrend[0]);
    monthlyChips.push(`Total ${sum(monthlyTrend).toLocaleString()}`, `Best ${formatMonthShort(best.month)} (${best.count})`);
  }

  const initialLoad = loading && !data;
  const updatedAt = data?.generated_at
    ? new Date(data.generated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <Container fluid className={`min-vh-100 p-3 p-md-4 anp-page ${isDark ? "dark-theme" : "light-theme"}`}>
      <NavMenu />

      <div className="anp-hero anp-fade">
        <div className="anp-hero-text">
          <span className="anp-hero-chip"><MdQueryStats /> Live analytics</span>
          <h1 className="anp-hero-title">Data Analytics</h1>
          <p className="anp-hero-sub">Trends, channels, SLA and team performance across all enquiries.</p>
        </div>

        <div className="anp-hero-actions">
          {updatedAt && <span className="anp-hero-updated">Updated {updatedAt}</span>}
          <Button className="anp-hero-btn" onClick={() => fetchAnalytics(currentFetchParams())} disabled={loading}>
            {loading ? <Spinner size="sm" className="me-2" /> : <BiRefresh className="me-2" />}
            Refresh
          </Button>
        </div>
      </div>

      <div className="anp-card anp-filter anp-fade" style={{ "--d": "60ms" }}>
        <div className="anp-filter-group">
          <span className="anp-filter-label" id="anp-range-label">Date range</span>
          <div className="anp-seg" role="group" aria-labelledby="anp-range-label">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                aria-pressed={!isCustomActive && rangeDays === opt.days}
                onClick={() => handlePresetClick(opt.days)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="anp-filter-group">
          <span className="anp-filter-label">Custom</span>
          <label className="anp-date">
            <span>From</span>
            <input
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => handleCustomFromChange(e.target.value)}
            />
          </label>
          <label className="anp-date">
            <span>To</span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => handleCustomToChange(e.target.value)}
            />
          </label>
          {(customFrom || customTo) && (
            <Button className="anp-btn-ghost" size="sm" onClick={clearCustomRange}>
              Clear
            </Button>
          )}
        </div>

        <p className="anp-filter-note">
          Every chart and stat below reflects {isCustomActive ? "" : "the last "}
          {rangeLabel}, compared against the {isCustomActive ? "same-length period" : rangeLabel} before that.
        </p>
      </div>

      {error && (
        <div className="anp-error" role="alert">
          <MdErrorOutline aria-hidden="true" />
          <span>{error}</span>
          <Button className="anp-btn-ghost" size="sm" onClick={() => fetchAnalytics(currentFetchParams())}>
            Try again
          </Button>
        </div>
      )}

      <div className={`anp-body ${loading && data ? "is-refreshing" : ""}`} aria-busy={loading}>
        <Row className="g-3 mb-3">
          <Col xs={12} sm={6} xl={3}>
            <StatTile
              accent="accent-blue"
              icon={<FaInbox />}
              delay={100}
              loading={initialLoad}
              label="Total Enquiries"
              value={formatCompact(kpis.total)}
              note={periodLabel}
              delta={kpis.totalDeltaPct}
              deltaGoodWhenUp
              deltaSuffix="%"
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <StatTile
              accent="accent-green"
              icon={<FaReply />}
              delay={150}
              loading={initialLoad}
              label="Response Rate"
              value={`${kpis.responseRatePct ?? 0}%`}
              note={`${formatCompact(kpis.responded)} replied`}
              delta={kpis.responseRateDeltaPts}
              deltaGoodWhenUp
              deltaSuffix="pt"
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <StatTile
              accent="accent-violet"
              icon={<FaBolt />}
              delay={200}
              loading={initialLoad}
              label="New Today"
              value={formatCompact(kpis.newToday)}
              note="Since midnight (SGT)"
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <StatTile
              accent="accent-magenta"
              icon={<FaClock />}
              delay={250}
              loading={initialLoad}
              label="Avg. Response Time"
              value={kpis.avgResponseDays != null ? formatDuration(kpis.avgResponseDays) : "-"}
              note="Submission to first reply"
              delta={kpis.avgResponseDeltaDays}
              deltaGoodWhenUp={false}
              deltaFormat={formatDuration}
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} xl={7}>
            <ChartCard
              title="Enquiries over time"
              subtitle={periodLabel}
              accent="accent-blue"
              delay={300}
              icon={<FaChartLine />}
              loading={initialLoad}
              tableRows={dailyTableRows}
              chips={dailyChips}
            >
              <LineChart data={dailyTrend} accentKey="blue" isDark={isDark} />
            </ChartCard>
          </Col>
          <Col xs={12} xl={5}>
            <ChartCard
              title="Enquiries by month"
              subtitle="Last 12 months"
              accent="accent-violet"
              delay={350}
              icon={<FaChartBar />}
              loading={initialLoad}
              tableRows={monthlyTableRows}
              chips={monthlyChips}
            >
              <VBarChart data={monthlyTrend} accentKey="violet" isDark={isDark} />
            </ChartCard>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} md={6} xl={3}>
            <ChartCard title="By Source" subtitle="Where enquiries come from" accent="accent-teal" delay={400} icon={<MdSource />} loading={initialLoad} tableRows={toRows(bySource)}>
              <HBarChart data={bySource} accentKey="teal" isDark={isDark} />
            </ChartCard>
          </Col>
          <Col xs={12} md={6} xl={3}>
            <ChartCard title="By Status" subtitle="Where they stand now" accent="accent-green" delay={430} icon={<MdOutlineFlag />} loading={initialLoad} tableRows={toRows(byStatus)}>
              <HBarChart data={byStatus} accentKey="green" isDark={isDark} />
            </ChartCard>
          </Col>
          <Col xs={12} md={6} xl={3}>
            <ChartCard title="By Country" subtitle="Top locations" accent="accent-magenta" delay={460} icon={<FaGlobeAsia />} loading={initialLoad} tableRows={toRows(byCountry)}>
              <HBarChart data={byCountry} accentKey="magenta" isDark={isDark} />
            </ChartCard>
          </Col>
          <Col xs={12} md={6} xl={3}>
            <ChartCard title="By Tag" subtitle="How enquiries are labelled" accent="accent-blue" delay={490} icon={<FaTags />} loading={initialLoad} tableRows={toRows(byTag)}>
              <HBarChart data={byTag} accentKey="blue" isDark={isDark} />
            </ChartCard>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} md={4}>
            <ChartCard title="Pipeline - By Priority" subtitle="Urgency mix" accent="accent-violet" delay={520} icon={<FaTasks />} loading={initialLoad} tableRows={toRows(byPriority)}>
              <HBarChart data={byPriority} accentKey="violet" isDark={isDark} />
            </ChartCard>
          </Col>
          <Col xs={12} md={4}>
            <ChartCard title="Response Time SLA" subtitle="How fast we first reply" accent="accent-teal" delay={550} icon={<FaUserClock />} loading={initialLoad} tableRows={toRows(responseTimeDistribution)}>
              <HBarChart data={responseTimeDistribution} accentKey="teal" isDark={isDark} />
            </ChartCard>
          </Col>
          <Col xs={12} md={4}>
            <ChartCard title="Team Performance" subtitle="Replies sent per person" accent="accent-green" delay={580} icon={<FaUsers />} loading={initialLoad} tableRows={toRows(byStaff)} valueLabel="Replies sent">
              <HBarChart data={byStaff} accentKey="green" isDark={isDark} />
            </ChartCard>
          </Col>
        </Row>

        {data?.generated_at && (
          <div className="anp-footer">
            Generated at {formatTimestamp(data.generated_at)}
            <span className="anp-live">Live</span>
          </div>
        )}
      </div>
    </Container>
  );
};

export default Analytics;
